import { useCallback, useRef } from 'react';
import type { AnnotationShape, AnnotationTool } from '../types';
import type { AnnotationAction } from '../annotationReducer';
import { normalizeRect, clamp } from '../utils/geometry';
import { simplifyPath } from '../utils/pathSimplify';
import { createDefaultShape } from '../utils/createDefaultShape';

const FREEFORM_SIMPLIFY_TOLERANCE = 0.002;
const FREEFORM_MERGE_DISTANCE = 0.05;
const FREEFORM_STROKE_WIDTH = 3;
const FREEFORM_MIN_DIST_SQ = 0.000004; // ~2px at 1000px — skip points closer than this during drawing
const MIN_SHAPE_SIZE = 0.005;
const DEFAULT_TEXT_WIDTH = 0.2;
const DEFAULT_TEXT_HEIGHT = 0.06;
const DEFAULT_FONT_SIZE_PX = 16;

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

interface UseDrawingOptions {
  activeTool: AnnotationTool;
  activeColor: string;
  shapes: AnnotationShape[];
  dispatch: React.Dispatch<AnnotationAction>;
  containerWidth: number;
  containerHeight: number;
  /** Current zoom factor (from data-zoom). Text fontSize is divided by
   *  this so the stored value represents size at zoom=1. */
  zoom?: number;
}

interface PointerState {
  originX: number;
  originY: number;
  shapeId: string;
}

function distanceBetween(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

export function useDrawing({ activeTool, activeColor, shapes, dispatch, containerWidth, containerHeight, zoom = 1 }: UseDrawingOptions) {
  const pointerState = useRef<PointerState | null>(null);
  const freeformPoints = useRef<Array<{ x: number; y: number }>>([]);
  const rafId = useRef<number>(0);
  const pendingShape = useRef<AnnotationShape | null>(null);

  const getSvgPoint = useCallback((e: React.PointerEvent<SVGSVGElement>): { x: number; y: number } => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    return {
      x: clamp((e.clientX - rect.left) / rect.width, 0, 1),
      y: clamp((e.clientY - rect.top) / rect.height, 0, 1),
    };
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (activeTool === 'eraser') return;
      if (e.button !== 0) return;

      // Capture the pointer so all subsequent move/up events are delivered
      // to this element even if the finger/pen moves outside the SVG
      // (critical for touch devices where the browser may otherwise
      // cancel the pointer sequence).
      e.currentTarget.setPointerCapture(e.pointerId);

      const pt = getSvgPoint(e);
      const id = generateId();

      pointerState.current = { originX: pt.x, originY: pt.y, shapeId: id };

      let shape: AnnotationShape;

      switch (activeTool) {
        case 'box':
          shape = {
            id,
            type: 'box',
            color: activeColor,
            x: pt.x,
            y: pt.y,
            width: 0,
            height: 0,
          };
          break;
        case 'circle':
          shape = {
            id,
            type: 'circle',
            color: activeColor,
            cx: pt.x,
            cy: pt.y,
            rx: 0,
            ry: 0,
          };
          break;
        case 'line':
          shape = {
            id,
            type: 'line',
            color: activeColor,
            x1: pt.x,
            y1: pt.y,
            x2: pt.x,
            y2: pt.y,
          };
          break;
        case 'arrow':
          shape = {
            id,
            type: 'arrow',
            color: activeColor,
            x1: pt.x,
            y1: pt.y,
            x2: pt.x,
            y2: pt.y,
          };
          break;
        case 'freeform':
          freeformPoints.current = [{ x: pt.x, y: pt.y }];
          shape = {
            id,
            type: 'freeform',
            color: activeColor,
            points: [{ x: pt.x, y: pt.y }],
            strokeWidth: FREEFORM_STROKE_WIDTH,
          };
          break;
        case 'text':
          shape = {
            id,
            type: 'text',
            color: activeColor,
            x: pt.x,
            y: pt.y,
            width: 0,
            height: 0,
            text: '',
            fontSize: DEFAULT_FONT_SIZE_PX / (zoom || 1),
          };
          break;
        default:
          return;
      }

      dispatch({ type: 'START_DRAWING', shape });
    },
    [activeTool, activeColor, dispatch, getSvgPoint],
  );

  const flushPendingShape = useCallback(() => {
    if (pendingShape.current) {
      dispatch({ type: 'UPDATE_DRAWING', shape: pendingShape.current });
      pendingShape.current = null;
    }
    rafId.current = 0;
  }, [dispatch]);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const state = pointerState.current;
      if (!state) return;

      const pt = getSvgPoint(e);
      const shift = e.shiftKey;

      let shape: AnnotationShape;

      switch (activeTool) {
        case 'box':
        case 'text': {
          let endX = pt.x;
          let endY = pt.y;
          // Shift: constrain to visually perfect square (compensate for non-square aspect ratio)
          if (shift && containerWidth > 0 && containerHeight > 0) {
            const dxPx = Math.abs(pt.x - state.originX) * containerWidth;
            const dyPx = Math.abs(pt.y - state.originY) * containerHeight;
            const sidePx = Math.max(dxPx, dyPx);
            endX = state.originX + (pt.x >= state.originX ? 1 : -1) * (sidePx / containerWidth);
            endY = state.originY + (pt.y >= state.originY ? 1 : -1) * (sidePx / containerHeight);
          }
          const { x, y, width, height } = normalizeRect(state.originX, state.originY, endX, endY);
          if (activeTool === 'text') {
            shape = {
              id: state.shapeId,
              type: 'text',
              color: activeColor,
              x, y, width, height,
              text: '',
              fontSize: DEFAULT_FONT_SIZE_PX / (zoom || 1),
            };
          } else {
            shape = {
              id: state.shapeId,
              type: 'box',
              color: activeColor,
              x, y, width, height,
            };
          }
          break;
        }
        case 'circle': {
          // Bounding-box drag: origin is one corner, pointer is the opposite.
          // The ellipse is inscribed in the bounding box (same as box tool).
          let endX = pt.x;
          let endY = pt.y;
          // Shift: constrain to perfect circle (compensate for non-square aspect ratio)
          if (shift && containerWidth > 0 && containerHeight > 0) {
            const dxPx = Math.abs(pt.x - state.originX) * containerWidth;
            const dyPx = Math.abs(pt.y - state.originY) * containerHeight;
            const sidePx = Math.max(dxPx, dyPx);
            endX = state.originX + (pt.x >= state.originX ? 1 : -1) * (sidePx / containerWidth);
            endY = state.originY + (pt.y >= state.originY ? 1 : -1) * (sidePx / containerHeight);
          }
          const cx = (state.originX + endX) / 2;
          const cy = (state.originY + endY) / 2;
          const rx = Math.abs(endX - state.originX) / 2;
          const ry = Math.abs(endY - state.originY) / 2;
          shape = {
            id: state.shapeId,
            type: 'circle',
            color: activeColor,
            cx,
            cy,
            rx,
            ry,
          };
          break;
        }
        case 'line':
        case 'arrow': {
          let x2 = pt.x;
          let y2 = pt.y;
          // Shift: snap to 45-degree angles
          if (shift) {
            const adx = pt.x - state.originX;
            const ady = pt.y - state.originY;
            const angle = Math.atan2(ady, adx);
            const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
            const dist = Math.sqrt(adx * adx + ady * ady);
            x2 = state.originX + dist * Math.cos(snapped);
            y2 = state.originY + dist * Math.sin(snapped);
          }
          shape = {
            id: state.shapeId,
            type: activeTool as 'line' | 'arrow',
            color: activeColor,
            x1: state.originX,
            y1: state.originY,
            x2,
            y2,
          };
          break;
        }
        case 'freeform': {
          // Skip points that are very close to the last one to reduce point count
          const pts = freeformPoints.current;
          const last = pts[pts.length - 1];
          const fdx = pt.x - last.x;
          const fdy = pt.y - last.y;
          if (fdx * fdx + fdy * fdy < FREEFORM_MIN_DIST_SQ) return;
          pts.push({ x: pt.x, y: pt.y });
          shape = {
            id: state.shapeId,
            type: 'freeform',
            color: activeColor,
            points: pts, // pass ref directly — avoids O(n) copy per move
            strokeWidth: FREEFORM_STROKE_WIDTH,
          };
          break;
        }
        default:
          return;
      }

      // Dispatch immediately — rAF throttling caused visible delay on fast drags
      dispatch({ type: 'UPDATE_DRAWING', shape });
    },
    [activeTool, activeColor, getSvgPoint, flushPendingShape, containerWidth, containerHeight],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const state = pointerState.current;
      if (!state) return;

      // Flush any pending rAF update before processing pointer up
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = 0;
        pendingShape.current = null;
      }

      pointerState.current = null;

      const pt = getSvgPoint(e);

      // Click without drag — create a default-sized shape centred on the click
      // point (WCAG 2.5.7 Dragging Movements — single-pointer alternative).
      // Freeform requires real drag input; text already has its own default
      // path further down.
      if (activeTool !== 'freeform' && activeTool !== 'text') {
        const dx = Math.abs(pt.x - state.originX);
        const dy = Math.abs(pt.y - state.originY);
        if (dx < MIN_SHAPE_SIZE && dy < MIN_SHAPE_SIZE) {
          const defaultShape = createDefaultShape(activeTool, activeColor, state.originX, state.originY, zoom);
          if (defaultShape) {
            // Reuse the in-progress drawing's id so undo still works coherently
            const shape = { ...defaultShape, id: state.shapeId };
            dispatch({ type: 'UPDATE_DRAWING', shape });
            dispatch({ type: 'COMMIT_DRAWING' });
          } else {
            dispatch({ type: 'CANCEL_DRAWING' });
          }
          return;
        }
      }

      if (activeTool === 'text') {
        const dx = Math.abs(pt.x - state.originX);
        const dy = Math.abs(pt.y - state.originY);
        if (dx < MIN_SHAPE_SIZE && dy < MIN_SHAPE_SIZE) {
          // Click without drag — create default-sized text box
          const finalShape: AnnotationShape = {
            id: state.shapeId,
            type: 'text',
            color: activeColor,
            x: state.originX,
            y: state.originY,
            width: DEFAULT_TEXT_WIDTH,
            height: DEFAULT_TEXT_HEIGHT,
            text: '',
            fontSize: DEFAULT_FONT_SIZE_PX / (zoom || 1),
          };
          dispatch({ type: 'UPDATE_DRAWING', shape: finalShape });
        }
        dispatch({ type: 'COMMIT_DRAWING' });
        dispatch({ type: 'START_TEXT_EDIT', id: state.shapeId });
        return;
      }

      if (activeTool === 'freeform') {
        // Simplify the path
        const simplified = simplifyPath(freeformPoints.current, FREEFORM_SIMPLIFY_TOLERANCE);

        if (simplified.length < 2) {
          dispatch({ type: 'CANCEL_DRAWING' });
          freeformPoints.current = [];
          return;
        }

        // Check if we should merge with the last freeform shape
        const lastShape = shapes[shapes.length - 1];
        if (
          lastShape &&
          lastShape.type === 'freeform' &&
          lastShape.color === activeColor &&
          distanceBetween(
            lastShape.points[lastShape.points.length - 1].x,
            lastShape.points[lastShape.points.length - 1].y,
            simplified[0].x,
            simplified[0].y,
          ) < FREEFORM_MERGE_DISTANCE
        ) {
          // Merge: cancel current drawing, update the existing shape via dispatch
          dispatch({ type: 'CANCEL_DRAWING' });
          const mergedPoints = [...lastShape.points, ...simplified];
          const mergedShape: AnnotationShape = {
            ...lastShape,
            points: mergedPoints,
          };
          dispatch({ type: 'RESIZE_SHAPE', id: lastShape.id, shape: mergedShape });
          freeformPoints.current = [];
          return;
        }

        // Update the drawing shape with simplified points before committing
        const finalShape: AnnotationShape = {
          id: state.shapeId,
          type: 'freeform',
          color: activeColor,
          points: simplified,
          strokeWidth: FREEFORM_STROKE_WIDTH,
        };
        dispatch({ type: 'UPDATE_DRAWING', shape: finalShape });
        freeformPoints.current = [];
      }

      dispatch({ type: 'COMMIT_DRAWING' });
    },
    [activeTool, activeColor, shapes, dispatch, getSvgPoint],
  );

  const isDrawing = useCallback(() => pointerState.current !== null, []);

  const cancelDrawing = useCallback(() => {
    if (!pointerState.current) return;
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
      rafId.current = 0;
      pendingShape.current = null;
    }
    pointerState.current = null;
    freeformPoints.current = [];
    dispatch({ type: 'CANCEL_DRAWING' });
  }, [dispatch]);

  /** Commit the in-progress drawing when the pointer leaves the canvas. */
  const finishDrawing = useCallback(() => {
    const state = pointerState.current;
    if (!state) return;

    // Flush any pending rAF update so the latest shape is in reducer state
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
      rafId.current = 0;
      if (pendingShape.current) {
        dispatch({ type: 'UPDATE_DRAWING', shape: pendingShape.current });
        pendingShape.current = null;
      }
    }

    pointerState.current = null;

    // For freeform, simplify the path before committing
    if (activeTool === 'freeform') {
      const simplified = simplifyPath(freeformPoints.current, FREEFORM_SIMPLIFY_TOLERANCE);
      freeformPoints.current = [];
      if (simplified.length < 2) {
        dispatch({ type: 'CANCEL_DRAWING' });
        return;
      }
      dispatch({
        type: 'UPDATE_DRAWING',
        shape: {
          id: state.shapeId,
          type: 'freeform',
          color: activeColor,
          points: simplified,
          strokeWidth: FREEFORM_STROKE_WIDTH,
        },
      });
      dispatch({ type: 'COMMIT_DRAWING' });
      return;
    }

    // For text, commit and start editing
    if (activeTool === 'text') {
      dispatch({ type: 'COMMIT_DRAWING' });
      dispatch({ type: 'START_TEXT_EDIT', id: state.shapeId });
      return;
    }

    // For box/circle/arrow — commit the current drawingShape as-is
    dispatch({ type: 'COMMIT_DRAWING' });
  }, [activeTool, activeColor, dispatch]);

  return { onPointerDown, onPointerMove, onPointerUp, isDrawing, cancelDrawing, finishDrawing };
}
